import React from "react"
import { FormProvider, UseFormReturn } from "react-hook-form"
import { useTranslation } from "react-i18next"

import { SelectOption } from "shared/types/Select"
import { FormOverlay } from "shared/ui/overlay/FormOverlay"

import { ProjectInput } from "entities/projects/ui/project-input/ProjectInput"
import { ProjectChildParentInput } from "widgets/inputs/project-child-parent-input/ProjectChildParentInput"
import { ProjectPositionInput } from "widgets/inputs/project-position-input/ProjectPositionInput"

export interface CopyProjectChildrenFormData {
  projectID: string | null
  parentID: string | null
  position: number
}

export interface CopyProjectChildrenOverlayViewModel {
  headline?: string

  autoFocus?: boolean

  methods: UseFormReturn<CopyProjectChildrenFormData>

  projectOptions: SelectOption<string>[]
  parentOptions: SelectOption<string>[]
  positionOptions: SelectOption<number>[]

  isLoadingProjects?: boolean
  isLoadingPositions?: boolean
  isSubmitting?: boolean

  hideParentInput?: boolean
  hidePositionInput?: boolean

  onSubmit: React.FormEventHandler
  onCancel: () => void
}

const FORM_ID = "copy-project-children-overlay-form"

export const MoveProjectChildOverlayView = React.memo(
  ({
    headline,
    methods,
    projectOptions,
    parentOptions,
    positionOptions,
    isLoadingProjects,
    isLoadingPositions,
    isSubmitting,
    hideParentInput,
    hidePositionInput,
    onSubmit,
    onCancel
  }: CopyProjectChildrenOverlayViewModel) => {
    const { t } = useTranslation()
    return (
      <FormOverlay
        onCancel={onCancel}
        headline={headline || t("ProjectChild.Actions.Copy")}
        id="copy-project-children-overlay"
        form={FORM_ID}
        className="copy-project-children-overlay"
        isLoading={isSubmitting}
        isSubmitDisabled={isSubmitting}
        showCloseButton
      >
        <FormProvider {...methods}>
          <form
            id={FORM_ID}
            onSubmit={onSubmit}
            className="overlay-form copy-project-children-overlay__form"
          >
            <ProjectInput
              items={projectOptions}
              loading={isLoadingProjects}
              required
            />
            {hidePositionInput ? null : (
              <div className="project-position-row">
                {hideParentInput ? null : (
                  <ProjectChildParentInput
                    options={parentOptions}
                    loading={isLoadingPositions}
                  />
                )}
                <ProjectPositionInput
                  name="position"
                  options={positionOptions}
                  loading={isLoadingPositions}
                />
              </div>
            )}
          </form>
        </FormProvider>
      </FormOverlay>
    )
  }
)
